import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Card, Text, Button,
  Spinner, FormItem, Input, Select, Textarea, Snackbar, Headline, Checkbox,
} from '@vkontakte/vkui';
import { Icon24Add, Icon24Write, Icon24Delete } from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import { api } from '../../api/client';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';
import { ModalRoot, ModalPageHeader } from '@vkontakte/vkui';
import { DragHandle } from '../../ui/DragHandle';
import type { SeatingTable, Guest } from '../../types';

const TYPE_LABEL: Record<string, string> = {
  head: 'Президиум',
  round: 'Круглый',
  square: 'Квадратный',
  rectangular: 'Прямоугольный',
};

const TYPE_ICON: Record<string, string> = {
  head: '👑',
  round: '⭕',
  square: '⬜',
  rectangular: '▭',
};

function loadColor(filled: number, cap: number): string {
  if (filled >= cap) return '#e33';
  if (filled >= cap * 0.75) return '#f0a030';
  return '#4bb34b';
}

type Modal = 'form' | 'confirm-delete' | 'guests' | 'menu-form' | 'menu-edit' | 'list-form' | null;
type Tab = 'tables' | 'menus' | 'lists';
type MenuCategory = 'dish' | 'drink' | 'dessert';
type MenuItem = { category: MenuCategory; name: string };

const CAT_LABEL: Record<MenuCategory, string> = {
  dish: 'Блюда',
  drink: 'Напитки',
  dessert: 'Десерты',
};

const CAT_ORDER: MenuCategory[] = ['dish', 'drink', 'dessert'];

export default function SeatingPage() {
  const navigate = useNavigate();
  const { currentWedding, guests, loadGuests, isLoading } = useWeddingStore();

  const [tab, setTab] = useState<Tab>('tables');
  const [tables, setTables] = useState<SeatingTable[]>([]);
  const [menus, setMenus] = useState<{ id: string; name: string; note?: string; items?: MenuItem[] }[]>([]);
  const [lists, setLists] = useState<{ id: string; name: string; note?: string }[]>([]);
  const [editingMenu, setEditingMenu] = useState<{ id: string; name: string; items: MenuItem[] } | null>(null);
  const [newItem, setNewItem] = useState<Record<MenuCategory, string>>({ dish: '', drink: '', dessert: '' });
  const [editItemIndex, setEditItemIndex] = useState<number | null>(null);
  const [editItemName, setEditItemName] = useState('');
  const [openToken, setOpenToken] = useState(0);
  const [modal, setModal] = useState<Modal>(null);
  const [editing, setEditing] = useState<SeatingTable | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SeatingTable | null>(null);
  const [form, setForm] = useState({ name: '', type: 'round', seats: '8', note: '' });
  const [guestPicker, setGuestPicker] = useState<SeatingTable | null>(null);
  const [pickedGuests, setPickedGuests] = useState<Set<string>>(new Set());
  const [simpleName, setSimpleName] = useState('');
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  const base = currentWedding ? `weddings/${currentWedding.id}/seating` : '';

  async function loadAll() {
    if (!currentWedding) return;
    try {
      const t = await api.get<SeatingTable[]>(base).catch(() => []);
      setTables(t);
    } catch {
      setSnack('Не удалось загрузить столы');
    }
    try {
      const m = await api.get<{ id: string; name: string; note?: string; items?: MenuItem[] }[]>(`${base}/menus`);
      setMenus(m);
    } catch {
      setMenus([]);
    }
    try {
      const l = await api.get<{ id: string; name: string; note?: string }[]>(`${base}/lists`);
      setLists(l);
    } catch {
      setLists([]);
    }
  }

  useEffect(() => {
    loadGuests().catch(() => {});
    loadAll();
  }, [currentWedding?.id]);

  const guestsByTable = useMemo(() => {
    const m = new Map<string, Guest[]>();
    guests.forEach((g) => {
      const key = g.table_id || '';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(g);
    });
    return m;
  }, [guests]);

  const unseated = guestsByTable.get('') || [];

  function openCreate() {
    setEditing(null);
    setForm({ name: `Стол ${tables.length + 1}`, type: 'round', seats: '8', note: '' });
    setOpenToken((t) => t + 1);
    setModal('form');
  }

  function openEdit(t: SeatingTable) {
    setEditing(t);
    setForm({ name: t.name, type: t.type, seats: String(t.seats), note: t.note || '' });
    setOpenToken((n) => n + 1);
    setModal('form');
  }

  function openGuests(t: SeatingTable) {
    setGuestPicker(t);
    setPickedGuests(new Set((guestsByTable.get(t.id) || []).map((g) => g.id)));
    setOpenToken((n) => n + 1);
    setModal('guests');
  }

  async function handleSave() {
    if (!form.name.trim() || !currentWedding) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        seats: Number(form.seats) || 8,
        note: form.note.trim() || undefined,
      };
      if (editing) {
        await api.patch(`${base}/${editing.id}`, payload);
        setSnack('Стол сохранён');
      } else {
        await api.post(base, payload);
        setSnack('Стол добавлен');
      }
      setModal(null);
      setEditing(null);
      await loadAll();
      await loadGuests().catch(() => {});
    } catch (e) {
      console.error('seating save failed', e);
      setSnack(e instanceof Error ? e.message : 'Не удалось сохранить стол');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete || !currentWedding) return;
    try {
      await api.delete(`${base}/${confirmDelete.id}`);
      setSnack('Стол удалён');
      setConfirmDelete(null);
      setModal(null);
      await loadAll();
    } catch {
      setSnack('Не удалось удалить');
    }
  }

  /** Sync guest ↔ table */
  async function assign(guestId: string, tableId: string | null) {
    if (!currentWedding) return;
    try {
      await api.post(`${base}/assign`, { guest_id: guestId, table_id: tableId });
      await loadGuests().catch(() => {});
      await loadAll();
    } catch {
      setSnack('Не удалось рассадить');
    }
  }

  async function savePickedGuests() {
    if (!guestPicker || !currentWedding) return;
    setSaving(true);
    try {
      const current = new Set((guestsByTable.get(guestPicker.id) || []).map((g) => g.id));
      const toAdd = [...pickedGuests].filter((id) => !current.has(id));
      const toRemove = [...current].filter((id) => !pickedGuests.has(id));
      for (const id of toAdd) await assign(id, guestPicker.id);
      for (const id of toRemove) await assign(id, null);
      setModal(null);
      setGuestPicker(null);
      setSnack('Рассадка обновлена');
    } catch {
      setSnack('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }

  async function addSimple(kind: 'menus' | 'lists') {
    if (!simpleName.trim() || !currentWedding) return;
    try {
      await api.post(`${base}/${kind}`, { name: simpleName.trim() });
      setSimpleName('');
      setModal(null);
      await loadAll();
      setSnack('Добавлено');
    } catch {
      setSnack('Не удалось добавить');
    }
  }

  async function deleteMenu(id: string) {
    if (!currentWedding) return;
    try {
      await api.delete(`${base}/menus/${id}`);
      await loadAll();
      setSnack('Меню удалено');
    } catch (e) {
      setSnack(e instanceof Error ? e.message : 'Не удалось удалить меню');
    }
  }

  function openMenuEdit(m: { id: string; name: string; items?: MenuItem[] }) {
    setEditingMenu({ id: m.id, name: m.name, items: [...(m.items || [])] });
    setNewItem({ dish: '', drink: '', dessert: '' });
    setEditItemIndex(null);
    setEditItemName('');
    setOpenToken((t) => t + 1);
    setModal('menu-edit');
  }

  function addMenuItems() {
    if (!editingMenu) return;
    const added: MenuItem[] = [];
    CAT_ORDER.forEach((cat) => {
      const name = newItem[cat].trim();
      if (name) added.push({ category: cat, name });
    });
    if (added.length === 0) return;
    setEditingMenu({
      ...editingMenu,
      items: [...editingMenu.items, ...added],
    });
    setNewItem({ dish: '', drink: '', dessert: '' });
  }

  function removeMenuItem(index: number) {
    if (!editingMenu) return;
    setEditingMenu({
      ...editingMenu,
      items: editingMenu.items.filter((_, i) => i !== index),
    });
  }

  function startEditItem(index: number, name: string) {
    setEditItemIndex(index);
    setEditItemName(name);
  }

  function saveEditItem() {
    if (!editingMenu || editItemIndex == null) return;
    const name = editItemName.trim();
    if (!name) return;
    const items = editingMenu.items.map((it, i) => (i === editItemIndex ? { ...it, name } : it));
    setEditingMenu({ ...editingMenu, items });
    setEditItemIndex(null);
    setEditItemName('');
  }

  async function saveMenuItems() {
    if (!editingMenu || !currentWedding) return;
    setSaving(true);
    try {
      await api.patch(`${base}/menus/${editingMenu.id}`, {
        name: editingMenu.name,
        items: editingMenu.items,
      });
      setModal(null);
      setEditingMenu(null);
      await loadAll();
      setSnack('Меню сохранено');
    } catch (e) {
      setSnack(e instanceof Error ? e.message : 'Не удалось сохранить меню');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Рассадка
      </PanelHeader>

      {/* Tabs */}
      <Div style={{ display: 'flex', gap: 8, padding: 12 }}>
        {([
          ['tables', 'Столы'],
          ['menus', 'Меню'],
          ['lists', 'Списки'],
        ] as [Tab, string][]).map(([key, label]) => (
          <Button
            key={key}
            size="s"
            mode={tab === key ? 'primary' : 'secondary'}
            onClick={() => setTab(key)}
          >
            {label}
          </Button>
        ))}
      </Div>

      {isLoading && tables.length === 0 ? (
        <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
      ) : tab === 'tables' ? (
        <>
          <Group
            header={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px 4px' }}>
                <Text style={{ fontWeight: 600 }}>Столы ({tables.length})</Text>
                <Button size="s" mode="secondary" before={<Icon24Add />} onClick={openCreate}>Стол</Button>
              </div>
            }
          >
            {tables.length === 0 && (
              <Div style={{ textAlign: 'center', padding: 24 }}>
                <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Добавьте столы</Text>
              </Div>
            )}
            {tables.map((t) => {
              const at = guestsByTable.get(t.id) || [];
              const filled = at.length;
              return (
                <Card key={t.id} style={{ margin: '8px 12px', padding: 12 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%' }}>
                    <div style={{ fontSize: 28 }}>{TYPE_ICON[t.type] || '⭕'}</div>
                    <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                      <Headline level="2">{t.name}</Headline>
                      <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
                        {TYPE_LABEL[t.type] || t.type} · {filled} / {t.seats} мест
                      </Text>
                      <div style={{ height: 6, borderRadius: 3, background: loadColor(filled, t.seats), marginTop: 8, opacity: 0.85, width: '100%' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                      <Button size="s" mode="tertiary" onClick={() => openEdit(t)}><Icon24Write /></Button>
                      <Button size="s" mode="tertiary" onClick={() => { setConfirmDelete(t); setOpenToken((n) => n + 1); setModal('confirm-delete'); }}>
                        <Icon24Delete />
                      </Button>
                    </div>
                  </div>

                  {/* full card width guest list */}
                  <Text
                    style={{
                      display: 'block',
                      marginTop: 10,
                      width: '100%',
                      fontSize: 12,
                      lineHeight: 1.5,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {at.length === 0 ? (
                      <span style={{ color: 'var(--vkui--color_text_secondary)' }}>Пусто</span>
                    ) : (
                      at.map((g, idx) => (
                        <span key={g.id}>
                          {idx > 0 ? ', ' : ''}
                          {g.first_name} {g.last_name}
                          <span
                            style={{ color: 'var(--vkui--color_text_secondary)', cursor: 'pointer', marginLeft: 2 }}
                            onClick={() => void assign(g.id, null)}
                          >
                            ×
                          </span>
                        </span>
                      ))
                    )}
                  </Text>

                  <Button size="s" mode="outline" style={{ marginTop: 8, width: '100%' }} onClick={() => openGuests(t)}>
                    Гости за столом
                  </Button>
                </Card>
              );
            })}
          </Group>

          <Group header={<Text>Без стола ({unseated.length})</Text>}>
            {unseated.length === 0 ? (
              <Div><Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Все гости рассажены</Text></Div>
            ) : (
              unseated.map((g) => (
                <Div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text style={{ flex: 1 }}>{g.first_name} {g.last_name}</Text>
                  <Select
                    placeholder="Стол"
                    value=""
                    onChange={(e) => void assign(g.id, e.currentTarget.value)}
                    options={tables.map((t) => ({
                      label: `${t.name} (${(guestsByTable.get(t.id) || []).length}/${t.seats})`,
                      value: t.id,
                    }))}
                    style={{ minWidth: 160 }}
                  />
                </Div>
              ))
            )}
          </Group>
        </>
      ) : tab === 'menus' ? (
        <Group header={
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 4px' }}>
            <Text style={{ fontWeight: 600 }}>Меню ({menus.length})</Text>
            <Button size="s" mode="secondary" before={<Icon24Add />} onClick={() => { setSimpleName(''); setOpenToken((t) => t + 1); setModal('menu-form'); }}>Меню</Button>
          </div>
        }>
          {menus.map((m) => {
            const counts = {
              dish: (m.items || []).filter((i) => i.category === 'dish').length,
              drink: (m.items || []).filter((i) => i.category === 'drink').length,
              dessert: (m.items || []).filter((i) => i.category === 'dessert').length,
            };
            return (
              <Card key={m.id} style={{ margin: '8px 12px', padding: 12, cursor: 'pointer' }} onClick={() => openMenuEdit(m)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Headline level="2">{m.name}</Headline>
                    <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
                      Блюда: {counts.dish} · Напитки: {counts.drink} · Десерты: {counts.dessert}
                    </Text>
                  </div>
                  <Button size="s" mode="secondary" onClick={(e) => { e.stopPropagation(); openMenuEdit(m); }}>
                    Изменить
                  </Button>
                  <Button
                    size="s"
                    mode="tertiary"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Удалить меню «${m.name}»?`)) void deleteMenu(m.id);
                    }}
                  >
                    <Icon24Delete />
                  </Button>
                </div>
              </Card>
            );
          })}
          {menus.length === 0 && <Div><Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Варианты меню (например: стандарт, вегетарианское, детское)</Text></Div>}
        </Group>
      ) : (
        <Group header={
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 4px' }}>
            <Text style={{ fontWeight: 600 }}>Списки ({lists.length})</Text>
            <Button size="s" mode="secondary" before={<Icon24Add />} onClick={() => { setSimpleName(''); setOpenToken((t) => t + 1); setModal('list-form'); }}>Список</Button>
          </div>
        }>
          {lists.map((l) => (
            <Div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ flex: 1 }}>{l.name}{l.note ? ` · ${l.note}` : ''}</Text>
            </Div>
          ))}
          {lists.length === 0 && <Div><Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Списки рассадки / рассылок</Text></Div>}
        </Group>
      )}

      <ModalRoot
        activeModal={
          modal === 'form' ? sheetId('seating-form', openToken)
            : modal === 'confirm-delete' ? sheetId('seating-delete', openToken)
              : modal === 'guests' ? sheetId('seating-guests', openToken)
                : modal === 'menu-form' ? sheetId('menu-form', openToken)
                  : modal === 'menu-edit' ? sheetId('menu-edit', openToken)
                    : modal === 'list-form' ? sheetId('list-form', openToken) : null
        }
        onClose={() => { setModal(null); setEditing(null); setConfirmDelete(null); setGuestPicker(null); }}
      >
        <ResizableModalPage openToken={openToken} id="seating-form" onClose={() => { setModal(null); setEditing(null); }} settlingHeight={80}>
          <ModalPageHeader>{editing ? 'Редактировать стол' : 'Новый стол'}</ModalPageHeader>
          <DragHandle />
          <Group>
            <FormItem top="Название *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormItem>
            <FormItem top="Тип">
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.currentTarget.value })}
                options={[
                  { label: 'Круглый', value: 'round' },
                  { label: 'Квадратный', value: 'square' },
                  { label: 'Прямоугольный', value: 'rectangular' },
                  { label: 'Президиум', value: 'head' },
                ]} />
            </FormItem>
            <FormItem top="Мест"><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} /></FormItem>
            <FormItem top="Заметка"><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></FormItem>
            <Div style={{ display: 'flex', gap: 8 }}>
              <Button size="l" mode="primary" stretched disabled={saving} onClick={handleSave}>{saving ? '…' : 'Сохранить'}</Button>
              <Button size="l" mode="secondary" onClick={() => { setModal(null); setEditing(null); }}>Отмена</Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="seating-guests" onClose={() => { setModal(null); setGuestPicker(null); }} settlingHeight={80}>
          <ModalPageHeader>Гости · {guestPicker?.name}</ModalPageHeader>
          <DragHandle />
          <Group>
            {guests.map((g) => (
              <Checkbox
                key={g.id}
                checked={pickedGuests.has(g.id)}
                onChange={(e) => {
                  const next = new Set(pickedGuests);
                  if (e.currentTarget.checked) next.add(g.id);
                  else next.delete(g.id);
                  setPickedGuests(next);
                }}
              >
                {g.first_name} {g.last_name}
              </Checkbox>
            ))}
            <Div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Button size="l" mode="primary" stretched disabled={saving} onClick={savePickedGuests}>Сохранить</Button>
              <Button size="l" mode="secondary" onClick={() => { setModal(null); setGuestPicker(null); }}>Отмена</Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="menu-edit" onClose={() => { setModal(null); setEditingMenu(null); }} settlingHeight={80}>
          <ModalPageHeader>Меню · {editingMenu?.name}</ModalPageHeader>
          <DragHandle />
          <Group>
            {CAT_ORDER.map((cat) => {
              const all = editingMenu?.items || [];
              const catIndices = all
                .map((x, i) => ({ x, i }))
                .filter(({ x }) => x.category === cat);
              return (
                <div key={cat} style={{ padding: '8px 12px', borderBottom: '1px solid var(--vkui--color_separator)' }}>
                  <Headline level="2" style={{ marginBottom: 8 }}>{CAT_LABEL[cat]}</Headline>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {catIndices.map(({ x: it, i }) => (
                      <div
                        key={i}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: '1px solid var(--vkui--color_separator_primary, rgba(0,0,0,0.15))',
                          background: 'var(--vkui--color_background_content, #fff)',
                          backgroundColor: 'var(--app-card-background)',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                          cursor: 'pointer',
                        }}
                        onClick={() => startEditItem(i, it.name)}
                      >
                        {editItemIndex === i ? (
                          <>
                            <Input
                              style={{ width: 120, height: 28 }}
                              value={editItemName}
                              autoFocus
                              onChange={(e) => setEditItemName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditItem();
                                if (e.key === 'Escape') setEditItemIndex(null);
                              }}
                            />
                            <Button size="s" mode="primary" onClick={(e) => { e.stopPropagation(); saveEditItem(); }}>OK</Button>
                          </>
                        ) : (
                          <>
                            <Text style={{ fontSize: 14 }}>{it.name}</Text>
                            <span
                              style={{ color: 'var(--vkui--color_text_secondary)', cursor: 'pointer', fontSize: 14 }}
                              onClick={(e) => { e.stopPropagation(); removeMenuItem(i); }}
                            >
                              ×
                            </span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <Input
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        boxShadow: 'none',
                      }}
                      value={newItem[cat]}
                      onChange={(e) => setNewItem({ ...newItem, [cat]: e.target.value })}
                      placeholder={`Добавить ${cat === 'dish' ? 'блюдо' : cat === 'drink' ? 'напиток' : 'десерт'}`}
                    />
                    <Button size="s" mode="tertiary" onClick={addMenuItems}>+</Button>
                  </div>
                </div>
              );
            })}
            <Div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <Button size="l" mode="primary" stretched disabled={saving} onClick={saveMenuItems}>
                {saving ? '…' : 'Сохранить меню'}
              </Button>
              <Button
                size="l"
                mode="outline"
                onClick={() => {
                  if (editingMenu && window.confirm(`Удалить меню «${editingMenu.name}»?`)) {
                    void deleteMenu(editingMenu.id).then(() => {
                      setModal(null);
                      setEditingMenu(null);
                    });
                  }
                }}
              >
                Удалить меню
              </Button>
              <Button size="l" mode="secondary" onClick={() => { setModal(null); setEditingMenu(null); }}>Отмена</Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="menu-form" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>Новое меню</ModalPageHeader>
          <Group>
            <FormItem top="Название *"><Input value={simpleName} onChange={(e) => setSimpleName(e.target.value)} placeholder="Например: Вегетарианское" /></FormItem>
            <Button size="l" mode="primary" stretched onClick={() => void addSimple('menus')}>Добавить</Button>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="list-form" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>Новый список</ModalPageHeader>
          <Group>
            <FormItem top="Название *"><Input value={simpleName} onChange={(e) => setSimpleName(e.target.value)} placeholder="Например: VIP" /></FormItem>
            <Button size="l" mode="primary" stretched onClick={() => void addSimple('lists')}>Добавить</Button>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="seating-delete" onClose={() => { setModal(null); setConfirmDelete(null); }} settlingHeight={80}>
          <ModalPageHeader>Удалить стол?</ModalPageHeader>
          <Group>
            <Div>
              <Text style={{ marginBottom: 12 }}>{confirmDelete?.name}</Text>
              <Button size="l" mode="primary" stretched style={{ marginBottom: 8 }} onClick={handleDelete}>Удалить</Button>
              <Button size="l" mode="secondary" stretched onClick={() => { setModal(null); setConfirmDelete(null); }}>Отмена</Button>
            </Div>
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {snack && <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)}>{snack}</Snackbar>}
    </Panel>
  );
}
